<template>
  <experience-checkout-widget
    :code="selectedAttractionCode || false"
    :auto-select-first="false"
    :empty-state-message="emptyStateMessage"
    :cart-url="cartUrl"
    :checkout-url="checkoutUrl"
    :continue-url="continueUrl"
  >
    <div class="space-y-8 text-dark">
      <div class="max-w-3xl">
        <h1 class="mt-5 text-4xl font-semibold">{{ title }}</h1>
        <p class="mt-3 text-[16px] leading-7 text-dark/70">
          Choose an attraction first, then we will guide you through dates, times, and tickets.
        </p>
      </div>

      <div class="rounded-[32px] bg-light p-4 lg:p-6">
        <div class="flex flex-wrap gap-3">
          <button
            v-for="tab in availableTabs"
            :key="tab.key"
            type="button"
            class="inline-flex items-center gap-3 rounded-full px-5 py-3 text-sm font-semibold transition"
            :class="activeTab === tab.key ? 'bg-dark text-light' : 'bg-white text-dark hover:bg-primary-tone'"
            @click="changeTab(tab.key)"
          >
            <span>{{ tab.label }}</span>
            <span class="rounded-full px-2 py-1 text-xs" :class="activeTab === tab.key ? 'bg-light text-dark' : 'bg-light text-dark/70'">
              {{ groupedAttractions[tab.key]?.length ?? 0 }}
            </span>
          </button>
        </div>

        <div v-if="catalogLoading" class="mt-6 rounded-[28px] bg-white px-6 py-10 text-center text-dark/70">
          Loading attractions...
        </div>

        <div v-else-if="catalogLoadFailed" class="mt-6 rounded-[28px] bg-white px-6 py-10 text-center text-dark/70">
          <LoadError @retry="loadCatalog" />
        </div>

        <div v-else-if="activeTabAttractions.length === 0" class="mt-6 rounded-[28px] bg-white px-6 py-10 text-center text-dark/70">
          No attractions are available in this group right now.
        </div>

        <div v-else class="mt-6 grid gap-4 md:grid-cols-2">
          <button
            v-for="experience in activeTabAttractions"
            :key="experience.code"
            type="button"
            class="group text-left"
            @click="selectAttraction(experience)"
          >
            <article
              class="flex h-full flex-col overflow-hidden rounded-[28px] border bg-white transition"
              :class="selectedAttractionCode === experience.code ? 'border-dark shadow-[0_16px_50px_rgba(31,38,54,0.12)]' : 'border-transparent hover:-translate-y-1 hover:border-primary hover:shadow-[0_16px_50px_rgba(31,38,54,0.08)]'"
            >
              <div v-if="experience.image" class="relative aspect-[4/3] overflow-hidden">
                <img
                  :src="experience.image"
                  :alt="experience.title"
                  class="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                >
                <div class="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div
                  class="absolute bottom-5 left-5 z-10 flex h-12 w-12 items-center justify-center rounded-full"
                  :class="`bg-${experience.page_style}-primary`"
                >
                  <experience-icon :page-style="experience.page_style"></experience-icon>
                </div>
              </div>
              <div v-else class="relative flex aspect-[4/3] items-end overflow-hidden bg-primary-tone p-5">
                <div
                  class="flex h-12 w-12 items-center justify-center rounded-full"
                  :class="`bg-${experience.page_style}-primary`"
                >
                  <experience-icon :page-style="experience.page_style"></experience-icon>
                </div>
              </div>

              <div class="flex flex-1 flex-col px-5 pb-5 pt-4">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h2 class="text-xl font-semibold leading-tight">{{ experience.title }}</h2>
                    <p v-if="experience.short_address" class="mt-2 text-mdx text-dark/70">
                      {{ experience.short_address }}
                    </p>
                  </div>
                  <span
                    class="shrink-0 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em]"
                    :class="selectedAttractionCode === experience.code ? 'bg-primary text-dark' : 'bg-light text-dark/70'"
                  >
                    {{ selectedAttractionCode === experience.code ? 'Selected' : 'Select' }}
                  </span>
                </div>

                <div class="mt-6 flex items-center justify-between gap-4 border-t border-dashed border-primary pt-4 text-sm">
                  <span class="font-semibold uppercase tracking-[0.12em] text-dark/60">{{ tabLabel(experience.tabKey) }}</span>
                  <span class="font-semibold text-dark underline underline-offset-4">Book this attraction</span>
                </div>
              </div>
            </article>
          </button>
        </div>
      </div>

      <div
        v-if="selectedAttraction"
        class="flex flex-col gap-2 rounded-xl border border-dashed border-primary bg-white px-6 py-5"
      >
        <div>
          <p class="mt-2 text-lg font-semibold">Now choose a date, time, and tickets for {{ selectedAttraction.title }}.</p>
        </div>
        <div>
          <p class="text-md ">For more details <a
              v-if="selectedAttraction.link"
              :href="selectedAttraction.link"
              class="inline-flex items-center text-dark underline underline-offset-4"
          >
            View attraction page
          </a></p>
        </div>
      </div>
    </div>
  </experience-checkout-widget>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import ExperienceIcon from '../components/ExperienceIcon.vue'
import { reportLoadError } from '../helpers/network'
import { trackEvent } from '../helpers/track'
import ExperienceCheckoutWidget from './ExperienceCheckoutWidget.vue'
import LoadError from './components/LoadError.vue'
import { isEnabledFlag } from './helpers'
import { useCheckoutCartStore } from './store'

const props = defineProps({
  code: {
    type: [String, Boolean],
    default: false,
  },
  title: {
    type: String,
    default: 'Book Tickets',
  },
  cartUrl: {
    type: String,
    default: '/cart/',
  },
  checkoutUrl: {
    type: String,
    default: '/checkout/',
  },
  continueUrl: {
    type: String,
    default: '/visit-experiences/',
  },
})

const TAB_CONFIG = [
  { key: 'caves', label: 'Caves' },
  { key: 'lighthouses', label: 'Lighthouses' },
  { key: 'adventure', label: 'Adventure' },
]

const TAB_ORDER = {
  'capes-raptor-centre': 1,
  'forest-adventures': 2,
  'jewel-cave': 10,
  'lake-cave': 11,
  'mammoth-cave': 12,
  'ngilgi-cave': 13,
  'cape-naturaliste-lighthouse': 20,
  'cape-leeuwin-lighthouse': 21,
}

const store = useCheckoutCartStore()
const catalogLoading = ref(true)
const catalogLoadFailed = ref(false)
const activeTab = ref('caves')
const selectedAttractionCode = ref(false)

const experienceDetails = computed(() => (
  Array.isArray(window.bookingWidget?.experiences) ? window.bookingWidget.experiences : []
))

const resolveInitialCode = () => {
  const params = new URLSearchParams(window.location.search)
  return props.code || params.get('attraction') || false
}

const groupKeyForPageStyle = (pageStyle = 'default') => {
  if (pageStyle === 'caves') {
    return 'caves'
  }

  if (['lighthouse', 'lighthouses'].includes(pageStyle)) {
    return 'lighthouses'
  }

  return 'adventure'
}

const selectableAttractions = computed(() => {
  const detailsByCode = new Map(
    experienceDetails.value
      .filter((item) => item?.code)
      .map((item) => [item.code, item])
  )

  return store.attractions
    .filter((attraction) => attraction?.code && isEnabledFlag(attraction?.enabled_retail))
    .map((attraction) => {
      const details = detailsByCode.get(attraction.code) ?? {}
      const pageStyle = details.page_style ?? 'default'

      return {
        ...attraction,
        ...details,
        title: details.title ?? attraction.retail_title ?? attraction.title ?? attraction.code,
        short_address: details.short_address ?? attraction.retail_location ?? '',
        image: details.image ?? '',
        link: details.link ?? '',
        page_style: pageStyle,
        tabKey: groupKeyForPageStyle(pageStyle),
      }
    })
    .sort((left, right) => {
      const leftOrder = TAB_ORDER[left.code] ?? 999
      const rightOrder = TAB_ORDER[right.code] ?? 999

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder
      }

      return left.title.localeCompare(right.title)
    })
})

const groupedAttractions = computed(() => {
  return selectableAttractions.value.reduce((groups, attraction) => {
    groups[attraction.tabKey].push(attraction)
    return groups
  }, {
    caves: [],
    lighthouses: [],
    adventure: [],
  })
})

const availableTabs = computed(() => {
  return TAB_CONFIG.filter((tab) => (groupedAttractions.value[tab.key] ?? []).length > 0)
})

const activeTabAttractions = computed(() => groupedAttractions.value[activeTab.value] ?? [])

const selectedAttraction = computed(() => (
  selectableAttractions.value.find((attraction) => attraction.code === selectedAttractionCode.value) ?? null
))

const emptyStateMessage = computed(() => {
  return availableTabs.value.length > 0
    ? 'Choose an attraction on the left to begin booking.'
    : 'Attractions are not available for online booking right now.'
})

const tabLabel = (tabKey) => {
  return TAB_CONFIG.find((tab) => tab.key === tabKey)?.label ?? 'Experience'
}

const syncSelectedAttraction = (code = false) => {
  if (!code) {
    selectedAttractionCode.value = false
    return
  }

  selectedAttractionCode.value = selectableAttractions.value.some((attraction) => attraction.code === code)
    ? code
    : false
}

const selectAttraction = async (attraction) => {
  if (!attraction?.code) {
    return
  }

  selectedAttractionCode.value = attraction.code
  trackEvent(`booking-select-${attraction.code}`)

  await nextTick()

  if (window.matchMedia('(max-width: 1023px)').matches) {
    document.getElementById('booking-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

const changeTab = (tabKey) => {
  activeTab.value = tabKey

  if (selectedAttraction.value?.tabKey !== tabKey) {
    selectedAttractionCode.value = false
  }
}

watch(selectedAttraction, (attraction) => {
  if (attraction?.tabKey) {
    activeTab.value = attraction.tabKey
  }
})

watch(availableTabs, (tabs) => {
  if (!tabs.length) {
    activeTab.value = 'caves'
    return
  }

  if (!tabs.some((tab) => tab.key === activeTab.value)) {
    activeTab.value = tabs[0].key
  }
}, { immediate: true })

watch(() => props.code, (newCode) => {
  syncSelectedAttraction(newCode || false)
})

watch(selectableAttractions, () => {
  syncSelectedAttraction(selectedAttractionCode.value || resolveInitialCode())
}, { deep: true })

const loadCatalog = async () => {
  catalogLoading.value = true
  catalogLoadFailed.value = false

  try {
    await store.ensureLoaded()
    syncSelectedAttraction(resolveInitialCode())
  } catch (error) {
    catalogLoadFailed.value = true
    reportLoadError(error)
  } finally {
    catalogLoading.value = false
  }
}

onMounted(loadCatalog)
</script>
